import axios from 'axios';
import { get } from 'lodash-es';
import { from } from 'rxjs';

import { mapProductsFiltersToRequestBody } from '../../services/filtering/api-param-map-service';
import { PAGE_SIZE_250, FIRST_PAGE } from '../../services/pagination-service';
import { createCancelablePromise } from '../../services/promises/create-cancelable-promise';

export const getStockLevelsRaw = (itemsGroupedByInventorySourceIds) => {
  return axios.post('/inventory/v2/getstocklevels', {
    itemsGroupedByInventorySourceIds,
  });
};

export const getStockLevels = (itemsGroupedByInventorySourceIds) => {
  const request = getStockLevelsRaw(itemsGroupedByInventorySourceIds).then((response) => {
    const responseData = get(response, 'data', undefined);
    return responseData;
  });

  const data$ = from(request);
  return data$;
};

export const allocateFulfillmentPlans = (fulfillmentPlanIds, shipFromId = undefined, allowInventoryCheckToPreventAllocation = true) => {
  const request = axios
    .post('/nativeinventory/allocatefulfillmentplans', {
      fulfillmentPlanIds,
      shipFromId,
      allowInventoryCheckToPreventAllocation,
    })
    .then((response) => get(response, 'data', undefined));

  const data$ = from(request);
  return data$;
};
/**
 * Allocates fulfillment plans
 * @param {string[]} fulfillmentPlanIds - IDs of fulfillment plans to allocate
 * @param {string} [shipFromId] - Optional ship from ID
 * @param {boolean} [allowInventoryCheckToPreventAllocation] - Whether to allow inventory check
 * @returns {Promise<AllocationResponse>} Promise resolving to allocation response
 */
export const allocateFulfillmentPlansPromise = (
  fulfillmentPlanIds,
  shipFromId = undefined,
  allowInventoryCheckToPreventAllocation = true,
) => {
  return axios
    .post('/nativeinventory/allocatefulfillmentplans', {
      fulfillmentPlanIds,
      shipFromId,
      allowInventoryCheckToPreventAllocation,
    })
    .then((response) => get(response, 'data', undefined));
};

export const deallocateFulfillmentPlansPromise = (fulfillmentPlanIds) => {
  return axios
    .post('/nativeinventory/deallocatefulfillmentplans', {
      fulfillmentPlanIds,
    })
    .then((response) => get(response, 'data', undefined));
};

export const deallocateFulfillmentPlans = (fulfillmentPlanIds) => {
  const request = deallocateFulfillmentPlansPromise(fulfillmentPlanIds);
  const data$ = from(request);
  return data$;
};

const productFilterBuilder = (searchTerm = '', appliedFilters) => {
  const filtersObj = mapProductsFiltersToRequestBody(appliedFilters);
  if (searchTerm) {
    filtersObj.searchTerm = searchTerm;
  }
  return filtersObj;
};

export const getInventory = ({
  pageNumber = FIRST_PAGE,
  pageSize = PAGE_SIZE_250,
  orderBy = 'SKU',
  direction = 'Ascending',
  searchTerm = '',
  appliedFilters = [],
  includeAuditData = false,
}) => {
  const params = {
    page: {
      pageNumber,
      pageSize,
    },
    orderBy,
    direction,
    includeAuditData,
  };
  const getAndTransform = axios
    .post('/inventorygrid', {
      ...params,
      filter: productFilterBuilder(searchTerm, appliedFilters),
    })
    .then((data) => {
      return get(data, 'data', {});
    });

  return createCancelablePromise(getAndTransform);
};

export const getInventoryPromise = ({
  pageNumber = 1,
  pageSize = 250,
  orderBy = 'SKU',
  direction = 'Ascending',
  searchTerm = '',
  appliedFilters = [],
  includeAuditData = false,
}) => {
  const params = {
    page: {
      pageNumber,
      pageSize,
    },
    orderBy,
    direction,
    includeAuditData,
  };

  const getAndTransform = axios
    .post('/inventorygrid', {
      ...params,
      filter: productFilterBuilder(searchTerm, appliedFilters),
    })
    .then((data) => {
      return data?.data ?? {};
    });

  return from(getAndTransform);
};

export const deallocateProduct = (productId) => {
  const getAndTransform = axios.delete(`/inventory/productAllocations/${productId}`).then((data) => {
    return get(data, 'data', {});
  });
  return createCancelablePromise(getAndTransform);
};

export const getInventoryLocationsForProduct = (productId) => {
  const getAndTransform = axios.get(`/inventory/productInventoryLevels/${productId}`);
  return createCancelablePromise(getAndTransform);
};

export const getInventoryAllocationsForProduct = (productId) => {
  const getAndTransform = axios.get(`/inventory/productAllocations/${productId}`);
  return createCancelablePromise(getAndTransform);
};

export const addInventoryForProduct = (addInventoryProps) => {
  const request = axios.post('/inventory/addInventory', addInventoryProps).then((response) => get(response, 'data', undefined));
  return createCancelablePromise(request);
};

export const editLotForProduct = (lotUuid, editLotProps) => {
  const request = axios.put(`/inventory/editLot/${lotUuid}`, editLotProps).then((response) => get(response, 'data', undefined));
  return createCancelablePromise(request);
};

export const getInventoryWarehouses = () => {
  const getAndTransform = axios.get('/inventoryWarehouses');
  return createCancelablePromise(getAndTransform);
};

export const createNewInventoryWarehouse = (name) => {
  const request = axios.post('/inventoryWarehouses', { name }).then((response) => response.data);
  return createCancelablePromise(request);
};

export const updateInventoryWarehouse = (updatedInventoryWarehouse) => {
  const request = axios.put('/inventoryWarehouses', [updatedInventoryWarehouse]).then((response) => response.data);
  return createCancelablePromise(request);
};

export const deleteInventoryWarehouse = (inventoryWarehouseId) => {
  const request = axios
    .delete(`/inventoryWarehouses?inventoryWarehouseId=${inventoryWarehouseId}&force=true`)
    .then((response) => response.data);
  return createCancelablePromise(request);
};

export const getInventoryWarehouseLocations = () => {
  const getAndTransform = axios.get('/inventoryLocations');
  return createCancelablePromise(getAndTransform);
};

export const createNewInventoryWarehouseLocation = (newInventoryLocation) => {
  const request = axios.post('/inventoryLocations', newInventoryLocation).then((response) => response.data);
  return createCancelablePromise(request);
};

export const deleteInventoryWarehouseLocation = (inventoryLocationId) => {
  const request = axios.delete(`/inventoryLocations?inventoryLocationId=${inventoryLocationId}`).then((response) => response.data);
  return createCancelablePromise(request);
};

export const deleteInventoryWarehouseLocations = (inventoryLocationIds) => {
  const request = axios
    .delete('/inventoryLocations/batch', { data: inventoryLocationIds })
    .then((response) => ({ ...response.data, status: response.status }));
  return createCancelablePromise(request);
};

export const updateInventoryWarehouseLocation = (updatedInventoryLocation) => {
  const request = axios.put('/inventoryLocations', updatedInventoryLocation).then((response) => response.data);
  return createCancelablePromise(request);
};

export const updateSingleTote = (updatedTote, toteId) => {
  const request = axios.put(`/totes/${toteId}`, updatedTote).then((response) => response.data);
  return createCancelablePromise(request);
};

export const moveInventoryForProduct = (moveProps) => {
  const request = axios.post('/inventory/moveInventory', moveProps).then((response) => get(response, 'data', undefined));
  const data$ = from(request);
  return data$;
};

export const removeInventoryForProduct = (addInventoryProps) => {
  const request = axios.post('/inventory/removeInventory', addInventoryProps).then((response) => get(response, 'data', undefined));
  return createCancelablePromise(request);
};

export const getInventoryHistoryForProduct = (productId, inventoryLocationId, lotUuid = '') => {
  const baseUrl = `/inventory/inventoryHistory/${productId}?warehouseLocationId=${inventoryLocationId}`;

  const url = baseUrl.concat(lotUuid != null ? `&lotUuid=${lotUuid}` : '');
  const getAndTransform = axios.get(url);
  return createCancelablePromise(getAndTransform);
};

export const exportInventoryAsync = ({ exportAllProducts, inventoryWarehouseId }) => {
  const getAndTransform = axios
    .post('/inventory/ExportAsync', { exportAllProducts, inventoryWarehouseId })
    .then((response) => response.data);
  return from(getAndTransform);
};

export const exportLocationsInventoryAsync = ({ inventoryWarehouseId }) => {
  const getAndTransform = axios.post('/inventory/LocationsExportAsync', { inventoryWarehouseId }).then((response) => response.data);
  return from(getAndTransform);
};

export const importInventoryFromCsv = ({ file, selectedWarehouse, isTestImport }) => {
  const formData = new FormData();
  formData.append('CsvFile', file);
  formData.append('InventoryWarehouseId', selectedWarehouse.inventoryWarehouseId);
  formData.append('TestMode', isTestImport);

  const getAndTransform = axios.post('/inventory/Import', formData, {
    headers: {
      ContentType: 'multipart/form-data',
    },
  });
  const data$ = from(getAndTransform);
  return data$;
};

export const getImportLogFromJobGUID = (jobGuid) => {
  const getAndTransform = axios.get(`/inventory/importLog?jobGuid=${jobGuid}`).then((data) => data.data);
  return from(getAndTransform);
};

export const getInventoryAllocationStrategy = () => {
  const getAndTransform = axios.get('/allocationstrategies/');
  return createCancelablePromise(getAndTransform);
};

export const saveAllocationRules = (rules) => {
  const transform = axios.post('allocationstrategies', rules);
  return createCancelablePromise(transform);
};

export const deactivateAllocationRule = (allocationStrategyId, allocationRuleId, task) => {
  const transform = axios.post('allocationstrategies/deactivateRule', {
    allocationStrategyId,
    allocationRuleId,
    task,
  });
  return createCancelablePromise(transform);
};

export const deleteTotes = (toteIds) => {
  const request = axios
    .delete('/totes', {
      data: toteIds,
    })
    .then((response) => response.data);
  return createCancelablePromise(request);
};

export const getOrderVelocity = ({ skuIds, lookbackMonths = 24, warehouseId }) => {
  const requestBody = {
    skuIds,
    lookbackMonths,
    warehouseId,
  };
  const request = axios.post('/inventory/order-velocity', requestBody).then((response) => get(response, 'data', undefined));
  return createCancelablePromise(request);
};

export const getTotesForWarehouse = (warehouseId) => {
  const getAndTransform = axios.get(`totes/warehouse/${warehouseId}`).then((response) => response.data);
  return createCancelablePromise(getAndTransform);
};

export const createNewTotes = (totes, shouldReturnPrintInfo = false) => {
  const requestBody = {
    totes,
    ...(shouldReturnPrintInfo && { ShouldReturnPrintInfo: true }),
  };
  const getAndTransform = axios.post('totes', requestBody).then((response) => response.data);
  return createCancelablePromise(getAndTransform);
};

export const getAllTotes = () => {
  const getAndTransform = axios.get('/totes').then((response) => response.data);
  return createCancelablePromise(getAndTransform);
};

export const exportTotesAsync = ({ warehouseId }) => {
  const getAndTransform = axios.post(`/totes/exportAsync/${warehouseId}`).then((response) => response.data);
  return from(getAndTransform);
};

export const getInventoryLevelByProductAndWarehouse = (productId, inventoryWarehouseId) => {
  const getAndTransform = axios
    .get(`/inventory/productInventoryLevels/${productId}/warehouse/${inventoryWarehouseId}`)
    .then((response) => response.data);
  return createCancelablePromise(getAndTransform);
};

export const getInventoryLevelsByWarehouse = (productIds, warehouseId) => {
  const request = axios
    .post('/inventory/inventoryLevelsByWarehouse', {
      productIds,
      inventoryWarehouseId: warehouseId,
    })
    .then((response) => response?.data ?? { inventoryLevels: [] });
  return createCancelablePromise(request);
};
